import { useEffect, useState } from 'react';
import { sponsorLogo, type Sponsor } from '@rabbitfuel/shared';

/*
 * Die Farbe eines Sponsors fuer die L-Form.
 *
 * Hat die Regie keine eingestellt, nimmt jeder Player sie aus dem Logo: das
 * Bild wird klein gerechnet, und von den kraeftigen Farben gewinnt die, die
 * am meisten Flaeche hat. Weiss, Schwarz, Grau und Durchsichtiges zaehlen
 * nicht -- ein weisses Logo auf durchsichtigem Grund hat keine Farbe, dann
 * bleibt das L dunkel.
 *
 * Gerechnet wird in Faechern zu je 16 Stufen und am Ende gemittelt: zwei
 * Browser, die das Bild minimal anders verkleinern, landen so trotzdem bei
 * derselben Farbe -- der Stream und die Zuschauer sehen dasselbe L.
 */

const cache = new Map<string, Promise<string | null>>();

const hex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;

function measure(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onerror = () => resolve(null);
    img.onload = () => {
      try {
        const size = 40;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        const buckets = new Map<number, { weight: number; r: number; g: number; b: number; n: number }>();
        let opaque = 0;
        let colorful = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 160) continue;
          opaque++;
          const [r, g, b] = [data[i], data[i + 1], data[i + 2]];
          const max = Math.max(r, g, b) / 255;
          const min = Math.min(r, g, b) / 255;
          const light = (max + min) / 2;
          const sat = max === min ? 0 : (max - min) / (1 - Math.abs(2 * light - 1));
          if (sat < 0.22 || light < 0.08 || light > 0.92) continue;
          colorful++;
          const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
          const slot = buckets.get(key) ?? { weight: 0, r: 0, g: 0, b: 0, n: 0 };
          // Kraeftige Farben zaehlen etwas mehr als blasse derselben Flaeche.
          slot.weight += 1 + sat;
          slot.r += r;
          slot.g += g;
          slot.b += b;
          slot.n++;
          buckets.set(key, slot);
        }
        // Ein paar farbige Pixel im Schriftzug machen noch keine Hausfarbe.
        if (!opaque || colorful / opaque < 0.04) return resolve(null);
        let best: { weight: number; r: number; g: number; b: number; n: number } | null = null;
        for (const slot of buckets.values()) if (!best || slot.weight > best.weight) best = slot;
        resolve(best ? hex(best.r / best.n, best.g / best.n, best.b / best.n) : null);
      } catch {
        resolve(null);
      }
    };
    img.src = url;
  });
}

/** Die Farbe aus einem Bild -- je Adresse nur einmal gerechnet. */
export function imageColor(url: string): Promise<string | null> {
  let pending = cache.get(url);
  if (!pending) {
    pending = measure(url);
    cache.set(url, pending);
  }
  return pending;
}

/** Woraus die Farbe kommt: das Logo, sonst das erste Bild des Sponsors. */
export function colorSource(sponsor: Sponsor): string | null {
  return sponsorLogo(sponsor) ?? sponsor.materials.find((m) => m.kind === 'image')?.url ?? null;
}

/** Die Farbe aus dem Logo -- unabhaengig davon, ob die Regie eine eigene gesetzt hat. */
export function useLogoColor(sponsor: Sponsor | null): string | null {
  const url = sponsor ? colorSource(sponsor) : null;
  const [found, setFound] = useState<{ url: string; color: string | null } | null>(null);
  useEffect(() => {
    if (!url) return;
    let alive = true;
    void imageColor(url).then((color) => alive && setFound({ url, color }));
    return () => {
      alive = false;
    };
  }, [url]);
  return found && found.url === url ? found.color : null;
}

/** Die Farbe, in der das L steht: die eingestellte, sonst die aus dem Logo -- oder null. */
export function useSponsorColor(sponsor: Sponsor | null): string | null {
  const auto = useLogoColor(sponsor && !sponsor.color ? sponsor : null);
  return sponsor?.color ?? auto;
}

/** Relative Helligkeit nach WCAG. */
function luminance(color: string): number {
  const n = parseInt(color.slice(1), 16);
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
}

/** Schrift auf dieser Farbe: Weiss oder fast Schwarz -- was sich besser abhebt. */
export function inkFor(color: string): string {
  const l = luminance(color);
  return (l + 0.05) / 0.0556 > 1.05 / (l + 0.05) ? '#15171a' : '#ffffff';
}
